/// <reference types="vite/client" />
/**
 * /audits — Y6 (#166) list of audit reports written to R2 by the
 * Integrator agent. Filters by the global engagement picker (Y5).
 */

import { useCallback, useEffect, useState } from "react";
import { ErrorBanner } from "../components/ErrorBanner";
import { useEngagement } from "../hooks/useEngagement";
import { renderMarkdown } from "../utils/render-markdown";

interface Dirigeant {
  nom?: string;
  prenoms?: string;
  qualite?: string;
  type_dirigeant?: string;
}

interface Opco {
  code?: string;
  name?: string;
  full_name?: string;
  website?: string;
}

interface Identity {
  legal_name?: string;
  name?: string;
  brand_name?: string;
  siret?: string;
  siren?: string;
  vat_number?: string;
  naf_code?: string;
  naf_libelle?: string;
  tranche_effectif_label?: string;
  nature_juridique_label?: string;
  legal_rep_name?: string;
  legal_rep_role?: string;
  date_creation?: string;
  etat_administratif?: string;
  address?: string;
  postal_code?: string;
  city?: string;
  dirigeants?: Dirigeant[];
  est_organisme_formation?: boolean;
  est_qualiopi?: boolean;
  idcc?: string;
  convention_name?: string;
  opco?: Opco;
  is_opco_funded?: boolean;
}

type AuditStatus =
  | "NEW"
  | "RUNNING"
  | "EMAIL_BLOCKED"
  | "SENT"
  | "REPLIED"
  | "WON"
  | "LOST"
  | "FAILED";

interface AuditEntry {
  key: string;
  engagement: string;
  mode: "full" | "dry-run-audit";
  uploaded: string;
  size: number;
  preview?: string;
  targetUrl?: string;
  prospectEmail?: string;
  displayName?: string;
  status: AuditStatus;
  source: string | null;
  leadId: string | null;
  identity?: Identity;
}

/**
 * Per-status badge palette, keyed off the theme status tokens. We pair a
 * token-driven text colour with a translucent surface + border derived
 * from the same hue so the badge reads clearly on the dark card.
 */
const STATUS_BADGES: Record<AuditStatus, { label: string; color: string }> = {
  NEW: { label: "NEW", color: "var(--color-status-idle)" },
  RUNNING: { label: "RUNNING", color: "var(--color-status-running)" },
  EMAIL_BLOCKED: { label: "EMAIL BLOCKED", color: "var(--color-status-paused)" },
  SENT: { label: "SENT", color: "var(--color-status-active)" },
  REPLIED: { label: "REPLIED", color: "var(--color-accent)" },
  WON: { label: "WON", color: "var(--color-status-active)" },
  LOST: { label: "LOST", color: "var(--color-status-error)" },
  FAILED: { label: "FAILED", color: "var(--color-status-error)" },
};

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Tous les statuts" },
  { value: "NEW", label: "New" },
  { value: "RUNNING", label: "Running" },
  { value: "EMAIL_BLOCKED", label: "Email bloqué" },
  { value: "SENT", label: "Envoyé" },
  { value: "REPLIED", label: "Répondu" },
  { value: "WON", label: "Gagné" },
  { value: "LOST", label: "Perdu" },
  { value: "FAILED", label: "Échec" },
];

const SOURCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Toutes les sources" },
  { value: "audit-gratuit", label: "Audit gratuit" },
  { value: "pharow", label: "Pharow" },
  { value: "manual", label: "Manuel" },
  { value: "referral", label: "Recommandation" },
];

function StatusBadge({ status }: { status: AuditStatus }) {
  const b = STATUS_BADGES[status];
  return (
    <span className="pill" style={{ color: b.color, borderColor: "var(--color-border)", background: "var(--color-bg-card)" }}>
      {b.label}
    </span>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * True when at least one of the Learn-enrich fields we actually display
 * is present. Guard against rendering the panel with only the email-only
 * fallback fields (name + website + flags), which add nothing the card
 * header doesn't already show.
 */
function hasIdentityContent(identity: Identity): boolean {
  return Boolean(
    identity.siret ||
      identity.siren ||
      identity.legal_name ||
      identity.naf_code ||
      identity.address ||
      identity.legal_rep_name ||
      (identity.dirigeants && identity.dirigeants.length > 0),
  );
}

function formatAddress(identity: Identity): string | null {
  const parts: string[] = [];
  if (identity.address) parts.push(identity.address);
  if (identity.postal_code || identity.city) {
    parts.push(`${identity.postal_code ?? ""} ${identity.city ?? ""}`.trim());
  }
  return parts.length > 0 ? parts.join(", ") : null;
}

function formatDirigeant(d: Dirigeant): string {
  const name = `${d.prenoms ?? ""} ${d.nom ?? ""}`.trim();
  if (!name) return d.qualite ?? "(dirigeant non identifié)";
  return d.qualite ? `${name} (${d.qualite})` : name;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}

const PAGE_SIZE = 10;

type DateRange = "all" | "today" | "week" | "month" | "quarter";

const DATE_RANGE_OPTIONS: Array<{ value: DateRange; label: string }> = [
  { value: "all", label: "Toutes les dates" },
  { value: "today", label: "Aujourd'hui" },
  { value: "week", label: "7 derniers jours" },
  { value: "month", label: "30 derniers jours" },
  { value: "quarter", label: "90 derniers jours" },
];

function sinceIsoForRange(range: DateRange): string | null {
  const now = Date.now();
  switch (range) {
    case "today": {
      // Local midnight — most useful for daily ops review.
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d.toISOString();
    }
    case "week":
      return new Date(now - 7 * 24 * 3600 * 1000).toISOString();
    case "month":
      return new Date(now - 30 * 24 * 3600 * 1000).toISOString();
    case "quarter":
      return new Date(now - 90 * 24 * 3600 * 1000).toISOString();
    case "all":
    default:
      return null;
  }
}

interface AuditsResponse {
  entries: AuditEntry[];
  total: number;
}

export function Audits() {
  const { engagementId } = useEngagement();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [dateRange, setDateRange] = useState<DateRange>("all");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reset to page 0 whenever a filter changes — the new result set has
  // a different total and "page N" might not exist anymore.
  useEffect(() => {
    setPage(0);
  }, [engagementId, dateRange, statusFilter, sourceFilter]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      // Empty engagementId = "Tous les engagements" — don't filter.
      if (engagementId) params.set("engagement", engagementId);
      const since = sinceIsoForRange(dateRange);
      if (since) params.set("since", since);
      if (statusFilter) params.set("status", statusFilter);
      if (sourceFilter) params.set("source", sourceFilter);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(page * PAGE_SIZE));
      fetch(`/api/audits/list?${params.toString()}`, { signal })
        .then(async (res) => {
          if (!res.ok) {
            const body = await res.text().catch(() => "");
            throw new Error(`HTTP ${res.status}${body ? ` — ${body.slice(0, 200)}` : ""}`);
          }
          return res.json() as Promise<AuditsResponse>;
        })
        .then((data) => {
          setEntries(data.entries ?? []);
          setTotal(data.total ?? 0);
          setLoading(false);
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") return;
          setError(err.message);
          setLoading(false);
        });
    },
    [engagementId, page, dateRange, statusFilter, sourceFilter],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  if (error && entries.length === 0) {
    return <ErrorBanner message={error} onRetry={load} context="audits" />;
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstShown = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastShown = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className="space-y-6 lg:space-y-8">
      <header className="reveal" style={{ animationDelay: "0ms" }}>
        <p className="eyebrow">Console · 07</p>
        <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
          <span className="text-[var(--color-text)]">Audit </span>
          <span className="text-accent-gradient">Reports</span>
        </h2>
        <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
          Markdown audit reports produced by the Integrator agent. Click "Download" to fetch the full file.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-[var(--color-text-muted)]">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">
            Période
          </span>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRange)}
            disabled={loading && entries.length === 0}
            className="font-mono text-xs px-3 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
            style={{
              background: "var(--color-bg-card)",
              color: "var(--color-text)",
              border: "1px solid var(--color-border)",
              minWidth: "180px",
            }}
          >
            {DATE_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-[var(--color-text-muted)]">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">
            Statut
          </span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            disabled={loading && entries.length === 0}
            className="font-mono text-xs px-3 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
            style={{
              background: "var(--color-bg-card)",
              color: "var(--color-text)",
              border: "1px solid var(--color-border)",
              minWidth: "160px",
            }}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-[var(--color-text-muted)]">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">
            Source
          </span>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            disabled={loading && entries.length === 0}
            className="font-mono text-xs px-3 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
            style={{
              background: "var(--color-bg-card)",
              color: "var(--color-text)",
              border: "1px solid var(--color-border)",
              minWidth: "160px",
            }}
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        {!loading && total > 0 && (
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">
            {total} audit{total > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {entries.length > 0 && <KpiStrip entries={entries} total={total} />}

      {loading && entries.length === 0 && (
        <div className="flex items-center gap-3 text-[var(--color-text-dim)]">
          <span className="live-dot text-[var(--color-accent)]" />
          <span className="font-mono text-xs tracking-[0.18em] uppercase blink">Loading audits…</span>
        </div>
      )}

      {!loading && entries.length === 0 && (
        <div className="panel p-12 text-center">
          <p className="eyebrow">No audits</p>
          <p className="font-display italic text-2xl mt-3 text-[var(--color-text-muted)]">
            No audit reports for this engagement yet.
          </p>
        </div>
      )}

      {entries.length > 0 && (
        <>
          <section className="space-y-3">
            {entries.map((e, i) => (
              <AuditCard key={e.key} entry={e} index={i} />
            ))}
          </section>

          {pageCount > 1 && (
            <nav className="flex items-center justify-between pt-4 border-t border-[var(--color-border)]">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">
                {firstShown}–{lastShown} sur {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                  className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: "var(--color-bg-card)",
                    color: "var(--color-text-muted)",
                    border: "1px solid var(--color-border)",
                  }}
                >
                  ← Précédent
                </button>
                <span className="font-mono text-xs text-[var(--color-text-muted)] tabular-nums px-2">
                  {page + 1} / {pageCount}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1 || loading}
                  className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: "var(--color-accent-soft)",
                    color: "var(--color-accent-glow)",
                    border: "1px solid var(--color-accent)",
                  }}
                >
                  Suivant →
                </button>
              </div>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

/**
 * KPI strip — computed over the currently-loaded page of entries.
 *   conversion = (REPLIED + WON) / SENT-or-beyond
 *   block rate = (EMAIL_BLOCKED + FAILED) / total-shown
 * "SENT-or-beyond" counts every audit that reached at least the SENT
 * stage (SENT, REPLIED, WON, LOST) so the denominator is the set that
 * actually got an email out.
 */
function KpiStrip({ entries, total }: { entries: AuditEntry[]; total: number }) {
  const sentOrBeyond = entries.filter((e) =>
    ["SENT", "REPLIED", "WON", "LOST"].includes(e.status),
  ).length;
  const replied = entries.filter((e) => e.status === "REPLIED" || e.status === "WON").length;
  const blocked = entries.filter((e) => e.status === "EMAIL_BLOCKED" || e.status === "FAILED").length;

  const conversion = sentOrBeyond > 0 ? Math.round((replied / sentOrBeyond) * 100) : null;
  const blockRate = entries.length > 0 ? Math.round((blocked / entries.length) * 100) : 0;

  const cards: Array<{ label: string; value: string; sub: string; accent?: boolean }> = [
    {
      label: "Audits (total)",
      value: String(total),
      sub: `${entries.length} affiché${entries.length > 1 ? "s" : ""}`,
    },
    {
      label: "Conversion",
      value: conversion === null ? "—" : `${conversion}%`,
      sub: "répondu+gagné / envoyés",
      accent: true,
    },
    {
      label: "Envoyés",
      value: String(sentOrBeyond),
      sub: "email parti (page)",
    },
    {
      label: "Bloqués / échecs",
      value: `${blockRate}%`,
      sub: `${blocked} sur la page`,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((c) => (
        <div
          key={c.label}
          className="panel p-4"
          style={c.accent ? { borderColor: "var(--color-accent)" } : undefined}
        >
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">
            {c.label}
          </p>
          <p
            className="font-display italic text-3xl mt-1 tabular-nums"
            style={{ color: c.accent ? "var(--color-accent-glow)" : "var(--color-text)" }}
          >
            {c.value}
          </p>
          <p className="font-mono text-[10px] text-[var(--color-text-muted)] mt-1">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

function AuditCard({ entry, index }: { entry: AuditEntry; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const [fullBody, setFullBody] = useState<string | null>(null);
  const [fetchState, setFetchState] = useState<"idle" | "loading" | "error">("idle");
  const [fetchErr, setFetchErr] = useState<string | null>(null);

  const toggle = useCallback(async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (fullBody !== null || fetchState === "loading") return;
    setFetchState("loading");
    setFetchErr(null);
    try {
      const res = await fetch(`/api/audits/get?key=${encodeURIComponent(entry.key)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      setFullBody(text);
      setFetchState("idle");
    } catch (err) {
      setFetchErr(err instanceof Error ? err.message : String(err));
      setFetchState("error");
    }
  }, [expanded, fullBody, fetchState, entry.key]);

  return (
    <article
      className="panel p-4 sm:p-5 reveal"
      style={{ animationDelay: `${Math.min(index * 40, 600)}ms` }}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3 className="font-display italic text-xl text-[var(--color-text)] flex-1 min-w-0 truncate">
          {entry.displayName || entry.engagement}
        </h3>
        <span
          className="pill"
          style={{
            color: entry.mode === "full" ? "var(--color-status-active)" : "var(--color-accent)",
            borderColor: entry.mode === "full" ? "rgba(132,204,22,0.4)" : "rgba(139,92,246,0.4)",
            background: entry.mode === "full" ? "rgba(132,204,22,0.08)" : "rgba(139,92,246,0.08)",
          }}
        >
          {entry.mode === "full" ? "FULL" : "PREVIEW"}
        </span>
        <StatusBadge status={entry.status} />
        {entry.source && (
          <span
            className="pill"
            style={{
              color: "var(--color-text-muted)",
              borderColor: "var(--color-border)",
              background: "var(--color-bg-card)",
            }}
            title="Source du lead"
          >
            {entry.source}
          </span>
        )}
        {entry.identity?.est_qualiopi && (
          <span
            className="pill"
            style={{
              color: "var(--color-status-active)",
              borderColor: "rgba(132,204,22,0.4)",
              background: "rgba(132,204,22,0.08)",
            }}
            title="Organisme de formation certifié Qualiopi"
          >
            QUALIOPI
          </span>
        )}
        {entry.identity?.nature_juridique_label && (
          <span
            className="pill"
            style={{
              color: "var(--color-text-muted)",
              borderColor: "var(--color-border)",
              background: "var(--color-bg-card)",
            }}
            title="Forme juridique (api.gouv)"
          >
            {entry.identity.nature_juridique_label}
          </span>
        )}
        <span className="font-mono text-[10px] text-[var(--color-text-dim)] tabular-nums">
          {formatDate(entry.uploaded)} · {formatBytes(entry.size)}
        </span>
        <a
          href={`/audits/${encodeURIComponent(entry.engagement)}`}
          className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded transition-colors"
          style={{
            background: "var(--color-accent-soft)",
            color: "var(--color-accent-glow)",
            border: "1px solid var(--color-accent)",
          }}
          title="Page de détail (timeline, rapport, relance)"
        >
          Détails →
        </a>
        <button
          type="button"
          onClick={toggle}
          className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded transition-colors"
          style={{
            background: expanded ? "var(--color-bg-card)" : "var(--color-bg-card)",
            color: "var(--color-text-muted)",
            border: "1px solid var(--color-border)",
          }}
        >
          {expanded ? "Réduire" : "Aperçu rapide"}
        </button>
        <a
          href={`/api/audits/get?key=${encodeURIComponent(entry.key)}`}
          target="_blank"
          rel="noreferrer"
          className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded transition-colors"
          style={{
            background: "var(--color-bg-card)",
            color: "var(--color-text-muted)",
            border: "1px solid var(--color-border)",
          }}
        >
          Download .md
        </a>
      </div>

      {(entry.targetUrl || entry.prospectEmail) && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-[var(--color-text-muted)]">
          {entry.targetUrl && (
            <span className="flex items-center gap-1.5 min-w-0">
              <svg className="w-3.5 h-3.5 flex-shrink-0 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 015.656 0l-1.414 1.414m-4.242 4.242l-1.414 1.414a4 4 0 11-5.656-5.656L8.172 10.172m1.414 4.242l4.242-4.242" />
              </svg>
              <a
                href={entry.targetUrl}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-[12px] truncate hover:text-[var(--color-accent-bright)] transition-colors"
              >
                {entry.targetUrl}
              </a>
            </span>
          )}
          {entry.prospectEmail && (
            <span className="flex items-center gap-1.5 min-w-0">
              <svg className="w-3.5 h-3.5 flex-shrink-0 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <a
                href={`mailto:${entry.prospectEmail}`}
                className="font-mono text-[12px] truncate hover:text-[var(--color-accent-bright)] transition-colors"
              >
                {entry.prospectEmail}
              </a>
            </span>
          )}
        </div>
      )}

      {!expanded && entry.preview && (
        <div
          className="mt-4 px-4 pt-2 pb-1 rounded relative overflow-hidden"
          style={{
            background: "var(--color-bg-card)",
            border: "1px solid var(--color-border)",
            maxHeight: "200px",
          }}
        >
          <div className="prose-preview">{renderMarkdown(entry.preview)}</div>
          {entry.size > entry.preview.length && (
            <div
              className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to bottom, transparent 0%, var(--color-bg-card) 90%)",
              }}
            />
          )}
        </div>
      )}

      {expanded && entry.identity && hasIdentityContent(entry.identity) && (
        <IdentityPanel identity={entry.identity} />
      )}

      {expanded && (
        <div
          className="mt-4 p-6 rounded"
          style={{
            background: "var(--color-bg-card)",
            border: "1px solid var(--color-border)",
          }}
        >
          {fetchState === "loading" && (
            <div className="flex items-center gap-3 text-[var(--color-text-dim)]">
              <span className="live-dot text-[var(--color-accent)]" />
              <span className="font-mono text-xs tracking-[0.18em] uppercase blink">Chargement du rapport…</span>
            </div>
          )}
          {fetchState === "error" && (
            <p className="text-sm text-[var(--color-status-error)]">
              Erreur de chargement : {fetchErr}. Réessayez ou utilisez "Download .md".
            </p>
          )}
          {fullBody !== null && renderMarkdown(fullBody)}
        </div>
      )}
    </article>
  );
}

function IdentityPanel({ identity }: { identity: Identity }) {
  const address = formatAddress(identity);
  return (
    <div
      className="mt-4 p-5 rounded"
      style={{
        background: "var(--color-bg-card)",
        border: "1px solid var(--color-border)",
      }}
    >
      <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] mb-3">
        Identité légale · api.gouv via Levier Learn
      </p>

      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        {identity.legal_name && (
          <Row label="Raison sociale">
            <span className="font-display italic text-[var(--color-text)]">
              {identity.legal_name}
            </span>
          </Row>
        )}
        {identity.brand_name && identity.brand_name !== identity.legal_name && (
          <Row label="Nom commercial">{identity.brand_name}</Row>
        )}
        {identity.siret && <Row label="SIRET" mono>{identity.siret}</Row>}
        {identity.siren && <Row label="SIREN" mono>{identity.siren}</Row>}
        {identity.vat_number && <Row label="TVA intracom." mono>{identity.vat_number}</Row>}
        {identity.nature_juridique_label && (
          <Row label="Forme juridique">{identity.nature_juridique_label}</Row>
        )}
        {identity.naf_code && (
          <Row label="NAF">
            <span className="font-mono">{identity.naf_code}</span>
            {identity.naf_libelle && (
              <span className="text-[var(--color-text-muted)]"> · {identity.naf_libelle}</span>
            )}
          </Row>
        )}
        {identity.tranche_effectif_label && (
          <Row label="Effectif">{identity.tranche_effectif_label}</Row>
        )}
        {identity.convention_name && (
          <Row label="Convention collective">
            <span className="text-[var(--color-text)]">{identity.convention_name}</span>
            {identity.idcc && (
              <span className="text-[var(--color-text-muted)] font-mono"> · IDCC {identity.idcc}</span>
            )}
          </Row>
        )}
        {identity.opco?.name && (
          <Row label="OPCO">
            {identity.opco.website ? (
              <a
                href={identity.opco.website}
                target="_blank"
                rel="noreferrer noopener"
                className="text-[var(--color-accent-bright)] hover:underline"
              >
                {identity.opco.name}
              </a>
            ) : (
              <span>{identity.opco.name}</span>
            )}
            {identity.is_opco_funded && (
              <span
                className="ml-2 font-mono text-[10px] uppercase tracking-[0.18em]"
                style={{ color: "var(--color-status-active)" }}
              >
                financement éligible
              </span>
            )}
          </Row>
        )}
        {identity.date_creation && (
          <Row label="Création">
            <span className="font-mono">{identity.date_creation}</span>
          </Row>
        )}
        {identity.etat_administratif && (
          <Row label="État">
            <span
              style={{
                color:
                  identity.etat_administratif === "A"
                    ? "var(--color-status-active)"
                    : "var(--color-status-error)",
              }}
            >
              {identity.etat_administratif === "A" ? "Actif" : "Cessé"}
            </span>
          </Row>
        )}
        {address && (
          <Row label="Siège social">
            <span className="text-[var(--color-text-muted)]">{address}</span>
          </Row>
        )}
        {identity.legal_rep_name && (
          <Row label="Représentant légal">
            <span>{identity.legal_rep_name}</span>
            {identity.legal_rep_role && (
              <span className="text-[var(--color-text-muted)]"> · {identity.legal_rep_role}</span>
            )}
          </Row>
        )}
      </dl>

      {identity.dirigeants && identity.dirigeants.length > 1 && (
        <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] mb-2">
            Tous les dirigeants ({identity.dirigeants.length})
          </p>
          <ul className="space-y-1 text-sm">
            {identity.dirigeants.map((d, i) => (
              <li key={i} className="text-[var(--color-text)]">
                {formatDirigeant(d)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  mono,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] mb-0.5">
        {label}
      </dt>
      <dd className={`text-[var(--color-text)] ${mono ? "font-mono" : ""}`}>{children}</dd>
    </div>
  );
}
